import { useEffect, useRef, useState } from "react";
import { useLive, post, del, get, ago } from "../lib/api";
import type { KnowledgeRow, KnowledgeKindInfo } from "../lib/api";
import { Card, Pill, Button, Empty, Carregando } from "../lib/ui";
import { CircleHelp, PenLine, Check } from "lucide-react";
import { Fotos } from "../lib/Fotos";

/** Uma pergunta que a IA não soube responder, agrupada por texto parecido. */
interface LacunaRow {
  id: string;
  tipo: "base_vazia" | "escalou";
  pergunta: string;
  vezes: number;
  ultima_em: string;
  lead_id: string | null;
  lead_name: string | null;
  ids: string[];
}

/**
 * A base de conhecimento.
 *
 * O que a IA sabe além do catálogo: como é cada bairro, regras de
 * financiamento, o que precisa passar pelo corretor. Sem isso ela tem duas
 * saídas ruins diante de uma pergunta simples — inventar, ou escalar tudo.
 */
export function Base() {
  const { data: itens, reload } = useLive<KnowledgeRow[]>("/knowledge", [
    "knowledge",
  ]);
  const { data: tipos } = useLive<KnowledgeKindInfo[]>("/knowledge/kinds", []);
  const [editando, setEditando] = useState<KnowledgeRow | "novo" | null>(null);
  const [fotosDe, setFotosDe] = useState<string | null>(null);

  if (!itens || !tipos) return <Carregando />;

  const porTipo = tipos.map((t) => ({
    ...t,
    itens: itens.filter((i) => i.kind === t.kind),
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-ink-3 max-w-prose">
          O que a IA responde sem inventar e sem incomodar o corretor. Textos de{" "}
          <span className="text-accent">região</span> entram na conversa
          sozinhos quando o lead cita o bairro — o resto ela consulta quando o
          assunto aparece. As <span className="text-accent">fotos</span> ficam
          aqui também: ela manda até 3 quando o cliente pede pra ver.
        </p>
        <div className="flex gap-2 shrink-0">
          <ImportarDocumento
            onExtraido={(texto, nome) =>
              setEditando({
                id: "",
                kind: "empreendimento",
                title: nome.replace(/\.[^.]+$/, ""),
                content: texto,
                neighborhood: null,
                tags: [],
                active: true,
                updated_at: "",
                fotos: 0,
                direto_ao_corretor: false,
                link: null,
              })
            }
          />
          <Button variant="primary" onClick={() => setEditando("novo")}>
            Adicionar
          </Button>
        </div>
      </div>

      <Lacunas
        onResponder={(pergunta) =>
          setEditando({
            id: "",
            kind: "faq",
            title: pergunta,
            content: "",
            neighborhood: null,
            tags: [],
            active: true,
            updated_at: "",
            fotos: 0,
            direto_ao_corretor: false,
            link: null,
          })
        }
      />

      {editando && (
        <Form
          item={editando === "novo" || editando.id === "" ? null : editando}
          rascunho={editando !== "novo" && editando.id === "" ? editando : null}
          tipos={tipos}
          onClose={() => setEditando(null)}
          onSaved={() => {
            setEditando(null);
            reload();
          }}
        />
      )}

      {itens.length === 0 && !editando && (
        <Empty>
          Base vazia. A IA não sabe nada sobre bairro nem financiamento — vai
          escalar toda pergunta simples pro corretor.
        </Empty>
      )}

      {porTipo
        .filter((g) => g.itens.length > 0)
        .map((g) => (
          <section key={g.kind} className="space-y-2">
            <div>
              <h3 className="text-sm font-medium">
                {g.rotulo}
                <span className="text-ink-3 font-normal ml-2">
                  {g.itens.length}
                </span>
              </h3>
              <p className="text-xs text-ink-3 mt-0.5">{g.ajuda}</p>
            </div>

            {g.itens.map((i) => (
              <Card key={i.id} className={`p-3 ${i.active ? "" : "opacity-50"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-ink">{i.title}</span>
                      {i.kind === "regiao" && (
                        <Pill tone="accent">entra sozinho</Pill>
                      )}
                      {!i.active && <Pill>desligado</Pill>}
                    </div>
                    <p className="text-xs text-ink-3 mt-1 line-clamp-2 leading-snug">
                      {i.content}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      onClick={() =>
                        setFotosDe((v) => (v === i.id ? null : i.id))
                      }
                    >
                      {i.fotos > 0 ? `${i.fotos} foto(s)` : "Fotos"}
                    </Button>
                    <Button onClick={() => setEditando(i)}>Editar</Button>
                    <Button
                      variant="danger"
                      onClick={async () => {
                        await del(`/knowledge/${i.id}`);
                        reload();
                      }}
                    >
                      Remover
                    </Button>
                  </div>
                </div>

                {fotosDe === i.id && (
                  <div className="mt-3 pt-3 border-t border-rule">
                    <Fotos
                      base={`/api/knowledge/${i.id}`}
                      onChange={reload}
                      dica="Fotos e vídeo do empreendimento — a IA manda até 3 quando o cliente pede pra ver, e o vídeo vai junto da abertura quando o imóvel está marcado como direto. Arraste aqui; a primeira leva a legenda."
                    />
                    {i.kind === "empreendimento" && <TestarEntrada id={i.id} />}
                  </div>
                )}
              </Card>
            ))}
          </section>
        ))}
    </div>
  );
}

/**
 * "Como fica a entrada deste imóvel?"
 *
 * Manda a abertura de verdade para um número seu, pelo caminho real: mesma
 * fila, mesma resolução do número no WhatsApp, mesmo prompt, mesmo material.
 * Ler o texto na tela não diz se o vídeo chega tocável nem como a mensagem
 * quebra em balões no celular.
 *
 * O lead entra marcado como simulação, fora de funil e de métrica, e um teste
 * novo no mesmo número substitui o anterior.
 */
function TestarEntrada({ id }: { id: string }) {
  const [telefone, setTelefone] = useState("");
  const [nome, setNome] = useState("");
  const [estado, setEstado] = useState<"parado" | "indo" | "foi">("parado");
  const [erro, setErro] = useState<string | null>(null);

  async function enviar() {
    setEstado("indo");
    setErro(null);
    try {
      const r = await post<{ ok?: boolean; error?: string }>(
        `/knowledge/${id}/testar`,
        { telefone, nome },
      );
      if (r.error) {
        setErro(r.error);
        setEstado("parado");
        return;
      }
      setEstado("foi");
      setTimeout(() => setEstado("parado"), 4000);
    } catch (e) {
      setErro((e as Error).message);
      setEstado("parado");
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-rule space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={telefone}
          onChange={(e) => setTelefone(e.target.value)}
          placeholder="47 99999-0000"
          className="h-9 px-3 text-sm w-40"
        />
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Nome (opcional)"
          className="h-9 px-3 text-sm w-40"
        />
        <Button
          onClick={enviar}
          disabled={estado === "indo" || !telefone.trim()}
        >
          {estado === "indo"
            ? "enviando…"
            : estado === "foi"
              ? "enviada"
              : "Enviar teste"}
        </Button>
      </div>
      {erro && <p className="text-xs text-danger">{erro}</p>}
      <p className="text-xs text-ink-3 leading-relaxed">
        Manda a abertura pra esse número agora, como se fosse um lead deste
        anúncio — com o material, se houver. Não respeita a janela de
        atendimento, porque é teste.
      </p>
    </div>
  );
}

/**
 * Sobe um PDF ou planilha e devolve o texto extraido para revisao.
 *
 * Nada e salvo direto: extracao de PDF nunca sai perfeita -- tabela vira
 * coluna embaralhada, cabecalho se repete a cada pagina -- e texto ruim na
 * base e pior que base vazia, porque a IA responde com confianca a partir de
 * lixo. O operador le antes.
 */
function ImportarDocumento({
  onExtraido,
}: {
  onExtraido: (texto: string, nome: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  async function enviar(f: File | null | undefined) {
    if (!f) return;
    setLendo(true);
    setErro(null);
    setAviso(null);
    try {
      const form = new FormData();
      form.append("documento", f);
      const res = await fetch("/api/documents/extract", {
        method: "POST",
        body: form,
      });
      const r = (await res.json()) as {
        texto?: string;
        aviso?: string;
        error?: string;
        filename?: string;
      };
      if (r.error) setErro(r.error);
      else if (r.texto) {
        if (r.aviso) setAviso(r.aviso);
        onExtraido(r.texto, r.filename ?? f.name);
      }
    } finally {
      setLendo(false);
      if (input.current) input.current.value = "";
    }
  }

  return (
    <>
      <Button onClick={() => input.current?.click()} disabled={lendo}>
        {lendo ? "lendo…" : "Importar documento"}
      </Button>
      <input
        ref={input}
        type="file"
        accept=".pdf,.xlsx,.xls,.csv,.txt,.md"
        onChange={(e) => void enviar(e.target.files?.[0])}
        className="hidden"
      />
      {(erro || aviso) && (
        <div
          className={`fixed bottom-4 right-4 max-w-sm p-3 rounded-lg border text-xs z-20 ${
            erro
              ? "bg-danger-dim border-danger/40 text-danger"
              : "bg-warm-dim border-warm/40 text-warm"
          }`}
        >
          {erro ?? aviso}
          <button
            onClick={() => {
              setErro(null);
              setAviso(null);
            }}
            className="ml-2 underline"
          >
            ok
          </button>
        </div>
      )}
    </>
  );
}

function Form({
  item,
  rascunho,
  tipos,
  onClose,
  onSaved,
}: {
  item: KnowledgeRow | null;
  rascunho?: KnowledgeRow | null;
  tipos: KnowledgeKindInfo[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const base = item ?? rascunho ?? null;
  const [kind, setKind] = useState(base?.kind ?? "regiao");
  const [title, setTitle] = useState(base?.title ?? "");
  const [content, setContent] = useState(base?.content ?? "");
  const [direto, setDireto] = useState(base?.direto_ao_corretor ?? false);
  const [link, setLink] = useState(base?.link ?? "");
  const [bairro, setBairro] = useState(base?.neighborhood ?? "");
  const [erro, setErro] = useState<string | null>(null);

  const campo =
    "w-full bg-surface-2 border border-rule-2 rounded-lg px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-accent outline-none";
  const info = tipos.find((t) => t.kind === kind);

  async function salvar() {
    const r = await post<{ error?: string }>("/knowledge", {
      id: item?.id,
      kind,
      title,
      content,
      diretoAoCorretor: direto,
      link,
      bairro,
    });
    if (r.error) {
      setErro(r.error);
      return;
    }
    onSaved();
  }

  return (
    <Card stripe="accent" className="p-4 space-y-3">
      <div className="grid sm:grid-cols-[200px_1fr] gap-3">
        <label className="space-y-1">
          <span className="text-xs text-ink-3">Tipo</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as KnowledgeRow["kind"])}
            className={campo}
          >
            {tipos.map((t) => (
              <option key={t.kind} value={t.kind}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-3">
            {kind === "regiao"
              ? "Nome do bairro — é ele que casa com o que o lead escreve"
              : "Título — descreva o assunto, pesa mais que o texto na busca"}
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={kind === "regiao" ? "Bucarein" : "Uso do FGTS"}
            className={campo}
          />
        </label>
      </div>

      {info && <p className="text-xs text-ink-3">{info.ajuda}</p>}

      {/* Imóvel específico: o anúncio já mostrou metragem e preço, e quem
          conduz dali é quem tem a tabela na mão. */}
      {kind === "empreendimento" && (
        <>
          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={direto}
              onChange={(e) => setDireto(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>
              <span className="text-ink">
                A IA só faz a abertura e passa pro corretor
              </span>
              <span className="block text-xs text-ink-3 mt-0.5 leading-relaxed">
                Para imóvel específico, em que o anúncio já mostrou metragem e
                preço. A IA cumprimenta, manda o material e escala — sem dizer
                o nome do imóvel, sem qualificar e sem oferecer horário.
              </span>
            </span>
          </label>

          {/* O link é a alternativa ao arquivo: vídeo grande demais pra
              subir, tour, página do imóvel. */}
          {direto && (
            <label className="space-y-1 block">
              <span className="text-xs text-ink-3">
                Link do imóvel — opcional, em vez do material ou junto dele
              </span>
              <input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://..."
                className={campo}
              />
              <span className="block text-xs text-ink-3 leading-relaxed">
                Vai como mensagem própria depois da abertura, e o WhatsApp monta
                a prévia — capa, título e site — então o lead vê do que se
                trata antes de abrir. Serve pra página do imóvel, tour ou
                vídeo no YouTube.
              </span>
            </label>
          )}

          {/* O bairro vira a última mensagem: é o gancho que o corretor
              encontra já respondido quando assume. */}
          {direto && (
            <label className="space-y-1 block">
              <span className="text-xs text-ink-3">
                Bairro — fecha a abertura com uma pergunta
              </span>
              <input
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                placeholder="Tabuleiro"
                className={campo}
              />
              <span className="block text-xs text-ink-3 leading-relaxed">
                Depois do material a IA manda:{" "}
                <span className="text-ink">
                  “
                  {bairro.trim()
                    ? `Você já conhece a região aqui do ${bairro.trim()}?`
                    : "Você já conhece a região?"}
                  ”
                </span>{" "}
                — e para por aí, esperando o corretor. Confira como a frase
                ficou: o nome do bairro entra como você escreveu.
              </span>
            </label>
          )}
        </>
      )}

      <label className="space-y-1 block">
        <span className="text-xs text-ink-3">
          Conteúdo — escreva como você explicaria pro cliente
        </span>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={8}
          placeholder={
            kind === "regiao"
              ? "Como é o bairro: trajeto, escola, comércio, perfil de quem mora. Inclua o ponto fraco honesto — a IA usa isso pra não prometer o que o bairro não tem."
              : "A resposta certa, com os detalhes que importam."
          }
          className={`${campo} leading-relaxed resize-y`}
        />
      </label>

      {erro && <p className="text-sm text-danger">{erro}</p>}

      <div className="flex gap-2">
        <Button variant="primary" onClick={salvar} disabled={!title || !content}>
          Salvar
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </Card>
  );
}

/**
 * O que a IA não soube responder.
 *
 * Fica em cima da base, e não numa aba própria, porque é aqui que a pessoa
 * resolve: lê a pergunta, escreve o texto uma vez, e a IA para de escalar
 * aquilo. Numa aba separada ninguém abriria.
 *
 * Agrupado por pergunta parecida e ordenado por repetição: três pessoas
 * perguntando a mesma coisa na semana valem mais que uma pergunta exótica.
 */
function Lacunas({
  onResponder,
}: {
  onResponder: (pergunta: string) => void;
}) {
  const [dados, setDados] = useState<{ itens: LacunaRow[]; total: number } | null>(
    null,
  );

  const recarregar = () =>
    get<{ itens: LacunaRow[]; total: number }>("/lacunas?dias=30")
      .then(setDados)
      .catch(() => setDados({ itens: [], total: 0 }));

  useEffect(() => {
    recarregar();
  }, []);

  if (!dados || dados.itens.length === 0) return null;

  async function resolver(ids: string[]) {
    await post("/lacunas/resolver", { ids });
    recarregar();
  }

  return (
    <Card className="p-5" stripe="warm">
      <div className="flex items-center gap-2">
        <CircleHelp size={15} className="text-warm" />
        <h3 className="text-sm font-semibold text-ink">
          A IA não soube responder {dados.itens.length}{" "}
          {dados.itens.length === 1 ? "coisa" : "coisas"}
        </h3>
      </div>
      <p className="text-xs text-ink-3 mt-1.5 max-w-prose leading-relaxed">
        Cada linha é um lead que esperou uma pessoa por algo que a base poderia
        responder. Escreva o texto uma vez e a IA para de escalar aquilo. Os
        últimos 30 dias.
      </p>

      <div className="mt-4 space-y-1.5">
        {dados.itens.map((l) => (
          <div
            key={l.id}
            className="flex flex-wrap items-center gap-2 text-sm rounded-lg bg-surface-2 border border-rule px-3 py-2"
          >
            <span className="text-ink flex-1 min-w-0">{l.pergunta}</span>
            {l.vezes > 1 && (
              <Pill tone="warm">{l.vezes}× perguntada</Pill>
            )}
            <Pill>{l.tipo === "escalou" ? "escalou" : "base vazia"}</Pill>
            <span className="text-[11px] text-ink-3">{ago(l.ultima_em)}</span>
            <Button
              className="h-8"
              icone={PenLine}
              onClick={() => onResponder(l.pergunta)}
            >
              Responder
            </Button>
            <Button
              variant="ghost"
              className="h-8"
              icone={Check}
              title="Já está resolvido, tirar da lista"
              onClick={() => resolver(l.ids)}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
